/**
 * Comprehensive utility functions for generationPrompt handling and validation
 * 
 * This module provides utilities for:
 * - Prompt validation and sanitization
 * - Display formatting and truncation
 * - Clipboard operations
 * - Prompt analysis and status detection
 * - Accessibility helpers
 * 
 * @author Generated for Tutr Frontend
 * @version 1.0.0
 */// ============================================================================
// Type Definitions and Interfaces
// ============================================================================
/**
 * Analysis result for a prompt containing various characteristics
 *//**
 * Status information for prompt display in UI components
 *//**
 * Content node data structure (minimal interface for this utility)
 */// ============================================================================
// Constants and Configuration
// ============================================================================
/** Maximum length for prompt preview */const DEFAULT_MAX_LENGTH=150;/** Default truncation suffix */const TRUNCATION_SUFFIX='...';/** Reading speed in words per minute for time estimation */const READING_SPEED_WPM=200;/** Prompt types that typically require generation prompts */const PROMPT_REQUIRED_TYPES=['lesson','exercise','quiz','explanation','generated_content'];/** Markdown detection patterns */const MARKDOWN_PATTERNS=[/^#{1,6}\s/m,// Headers
/\*\*.*?\*\*/,// Bold
/\*.*?\*/,// Italic
/`.*?`/,// Inline code
/```[\s\S]*?```/,// Code blocks
/^\s*[-*+]\s/m,// Lists
/^\s*\d+\.\s/m,// Numbered lists
/\[.*?\]\(.*?\)/// Links
];/** Code block detection pattern */const CODE_BLOCK_PATTERN=/```[\s\S]*?```|`[^`\n]+`/g;// ============================================================================
// Core Validation Functions
// ============================================================================
/**
 * Validates prompt data format and content
 * 
 * @param prompt - The prompt data to validate (can be any type)
 * @returns True if the prompt is valid, false otherwise
 * 
 * @example
 * ```typescript
 * const isValid = validatePromptData("Generate a lesson about..."); // true
 * const isInvalid = validatePromptData(null); // false
 * ```
 */export function validatePromptData(prompt){try{// Check for null, undefined, or empty values
if(prompt===null||prompt===undefined){return false;}// Handle string prompts
if(typeof prompt==='string'){return prompt.trim().length>0;}// Handle object prompts (structured format)
if(typeof prompt==='object'){// Check for common prompt object structures
if('content'in prompt&&typeof prompt.content==='string'){return prompt.content.trim().length>0;}if('text'in prompt&&typeof prompt.text==='string'){return prompt.text.trim().length>0;}if('prompt'in prompt&&typeof prompt.prompt==='string'){return prompt.prompt.trim().length>0;}// If it's an object but doesn't match expected structure
return false;}// All other types are invalid
return false;}catch(error){console.error('Error validating prompt data:',error);return false;}}/**
 * Sanitizes prompt data for safe display in UI
 * 
 * @param prompt - The prompt string to sanitize
 * @returns Sanitized prompt string safe for display
 * 
 * @example
 * ```typescript
 * const safe = sanitizePrompt("<script>alert('xss')</script>Hello");
 * // Returns: "&lt;script&gt;alert('xss')&lt;/script&gt;Hello"
 * ```
 */export function sanitizePrompt(prompt){try{if(!prompt||typeof prompt!=='string'){return'';}return prompt// HTML escape
.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#x27;').replace(/\//g,'&#x2F;')// Remove potential script injection patterns
.replace(/javascript:/gi,'').replace(/data:/gi,'').replace(/vbscript:/gi,'')// Normalize whitespace
.replace(/\s+/g,' ').trim();}catch(error){console.error('Error sanitizing prompt:',error);return'';}}/**
 * Detects string encoding issues that may indicate data corruption
 *
 * @param text - The text to analyze for encoding issues
 * @returns Object with hasIssues flag and array of detected issue types
 *
 * @example
 * ```typescript
 * const result = detectEncodingIssues("Some text with  replacement chars");
 * if (result.hasIssues) {
 *   console.log('Issues found:', result.issueTypes);
 * }
 * ```
 */export function detectEncodingIssues(text){const issues=[];try{if(!text||typeof text!=='string'){return{hasIssues:false,issueTypes:[]};}// Check for common encoding corruption patterns
if(text.includes(''))issues.push('replacement_character');if(text.includes('\uFFFD'))issues.push('unicode_replacement');if(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/.test(text))issues.push('control_characters');if(/[^\x00-\x7F]/.test(text)&&!/[\u00A0-\uFFFF]/.test(text))issues.push('invalid_unicode');return{hasIssues:issues.length>0,issueTypes:issues};}catch(error){console.error('Error detecting encoding issues:',error);return{hasIssues:true,issueTypes:['detection_error']};}}// ============================================================================
// Display and Formatting Functions
// ============================================================================
/**
 * Truncates long prompts with ellipsis for display
 * 
 * @param prompt - The prompt string to truncate
 * @param maxLength - Maximum length before truncation (default: 150)
 * @returns Truncated prompt with ellipsis if needed
 * 
 * @example
 * ```typescript
 * const short = truncatePrompt("This is a very long prompt...", 20);
 * // Returns: "This is a very lo..."
 * ```
 */export function truncatePrompt(prompt){let maxLength=arguments.length>1&&arguments[1]!==undefined?arguments[1]:DEFAULT_MAX_LENGTH;try{if(!prompt||typeof prompt!=='string'){return'';}if(maxLength<=0){return'';}if(prompt.length<=maxLength){return prompt;}// Try to break at word boundary
const truncated=prompt.substring(0,maxLength);const lastSpace=truncated.lastIndexOf(' ');if(lastSpace>maxLength*0.8){return truncated.substring(0,lastSpace)+TRUNCATION_SUFFIX;}return truncated+TRUNCATION_SUFFIX;}catch(error){console.error('Error truncating prompt:',error);return prompt||'';}}/**
 * Formats prompt for user-friendly display
 * 
 * @param prompt - The prompt string to format
 * @returns Formatted prompt string optimized for display
 * 
 * @example
 * ```typescript
 * const formatted = formatPromptForDisplay("  # Title\n\nContent  ");
 * // Returns properly formatted and cleaned prompt
 * ```
 */export function formatPromptForDisplay(prompt){try{if(!prompt||typeof prompt!=='string'){return'';}return prompt// Normalize line endings
.replace(/\r\n/g,'\n').replace(/\r/g,'\n')// Remove excessive whitespace but preserve structure
.replace(/\n{3,}/g,'\n\n').replace(/[ \t]+/g,' ')// Trim each line
.split('\n').map(line=>line.trim()).join('\n')// Final trim
.trim();}catch(error){console.error('Error formatting prompt for display:',error);return prompt||'';}}// ============================================================================
// Clipboard and Interaction Functions
// ============================================================================
/**
 * Copies prompt to clipboard with comprehensive error handling
 * 
 * @param prompt - The prompt string to copy
 * @returns Promise that resolves to true if successful, false otherwise
 * 
 * @example
 * ```typescript
 * const success = await copyPromptToClipboard("Prompt text");
 * if (success) {
 *   console.log("Copied to clipboard!");
 * }
 * ```
 */export async function copyPromptToClipboard(prompt){try{if(!prompt||typeof prompt!=='string'){console.warn('Invalid prompt provided for clipboard copy');return false;}// Check if clipboard API is available
if(!navigator.clipboard){// Fallback for older browsers
return await fallbackCopyToClipboard(prompt);}// Use modern clipboard API
await navigator.clipboard.writeText(prompt);console.info('Prompt copied to clipboard successfully');return true;}catch(error){console.error('Failed to copy prompt to clipboard:',error);// Try fallback method
try{return await fallbackCopyToClipboard(prompt);}catch(fallbackError){console.error('Fallback clipboard copy also failed:',fallbackError);return false;}}}/**
 * Fallback clipboard copy method for older browsers
 * 
 * @private
 * @param text - Text to copy
 * @returns Promise that resolves to success status
 */async function fallbackCopyToClipboard(text){const textArea=document.createElement('textarea');textArea.value=text;textArea.style.position='fixed';textArea.style.left='-999999px';textArea.style.top='-999999px';document.body.appendChild(textArea);textArea.focus();textArea.select();try{const successful=document.execCommand('copy');document.body.removeChild(textArea);return successful;}catch(error){document.body.removeChild(textArea);throw error;}}// ============================================================================
// Analysis and Intelligence Functions
// ============================================================================
/**
 * Analyzes prompt characteristics and provides detailed metrics
 * 
 * @param prompt - The prompt string to analyze
 * @returns Comprehensive analysis of the prompt
 * 
 * @example
 * ```typescript
 * const analysis = analyzePrompt("# Lesson Plan\n\nCreate a comprehensive...");
 * console.log(analysis.format); // "markdown"
 * console.log(analysis.complexity); // "moderate"
 * ```
 */export function analyzePrompt(prompt){try{if(!prompt||typeof prompt!=='string'){return createEmptyAnalysis();}const trimmedPrompt=prompt.trim();const words=trimmedPrompt.split(/\s+/).filter(word=>word.length>0);const lines=trimmedPrompt.split('\n');// Format detection
const format=detectPromptFormat(trimmedPrompt);// Code block detection
const codeBlocks=trimmedPrompt.match(CODE_BLOCK_PATTERN)||[];const hasCodeBlocks=codeBlocks.length>0;// Formatting detection
const hasFormatting=format==='markdown'||hasSpecialFormatting(trimmedPrompt);// Reading time estimation
const readingTimeMinutes=Math.ceil(words.length/READING_SPEED_WPM);// Complexity analysis
const complexity=determineComplexity(trimmedPrompt,words.length,hasCodeBlocks,hasFormatting);// Completeness check
const isComplete=checkCompleteness(trimmedPrompt);// Language hints
const languageHints=extractLanguageHints(trimmedPrompt,codeBlocks);return{length:trimmedPrompt.length,wordCount:words.length,lineCount:lines.length,format,hasCodeBlocks,hasFormatting,readingTimeMinutes,complexity,isComplete,languageHints};}catch(error){console.error('Error analyzing prompt:',error);return createEmptyAnalysis();}}/**
 * Creates an empty analysis object for error cases
 * 
 * @private
 * @returns Empty PromptAnalysis object
 */function createEmptyAnalysis(){return{length:0,wordCount:0,lineCount:0,format:'unknown',hasCodeBlocks:false,hasFormatting:false,readingTimeMinutes:0,complexity:'simple',isComplete:false,languageHints:[]};}/**
 * Detects the format of a prompt
 * 
 * @private
 * @param prompt - Prompt to analyze
 * @returns Detected format
 */function detectPromptFormat(prompt){// Check for markdown patterns
for(const pattern of MARKDOWN_PATTERNS){if(pattern.test(prompt)){return'markdown';}}// Check for structured patterns (JSON-like, YAML-like)
if(/^\s*[\{\[]/.test(prompt)||/^\s*\w+:\s*/.test(prompt)){return'structured';}// Default to plain text
return'plain';}/**
 * Checks for special formatting beyond markdown
 * 
 * @private
 * @param prompt - Prompt to check
 * @returns True if special formatting detected
 */function hasSpecialFormatting(prompt){return /[*_`#\[\](){}]/.test(prompt)||/^\s*[-*+]\s/m.test(prompt)||/^\s*\d+\.\s/m.test(prompt);}/**
 * Determines complexity level of a prompt
 * 
 * @private
 * @param prompt - Prompt text
 * @param wordCount - Number of words
 * @param hasCodeBlocks - Whether prompt contains code
 * @param hasFormatting - Whether prompt has formatting
 * @returns Complexity level
 */function determineComplexity(prompt,wordCount,hasCodeBlocks,hasFormatting){let complexityScore=0;// Word count factor
if(wordCount>100)complexityScore+=1;if(wordCount>300)complexityScore+=1;// Structural complexity
if(hasCodeBlocks)complexityScore+=1;if(hasFormatting)complexityScore+=1;// Content complexity indicators
const complexPatterns=[/\b(algorithm|implementation|architecture|framework)\b/i,/\b(analyze|evaluate|compare|synthesize)\b/i,/\b(multiple|various|several|different)\b/i];for(const pattern of complexPatterns){if(pattern.test(prompt)){complexityScore+=1;}}if(complexityScore>=4)return'complex';if(complexityScore>=2)return'moderate';return'simple';}/**
 * Checks if a prompt appears to be complete
 * 
 * @private
 * @param prompt - Prompt to check
 * @returns True if prompt appears complete
 */function checkCompleteness(prompt){// Check for incomplete indicators
const incompletePatterns=[/\.\.\.\s*$/,// Ends with ellipsis
/\btodo\b/i,// Contains TODO
/\[incomplete\]/i,// Marked as incomplete
/\[tbc\]/i,// To be continued
/^\s*$/// Empty or whitespace only
];for(const pattern of incompletePatterns){if(pattern.test(prompt)){return false;}}// Check for completion indicators
const completionPatterns=[/[.!?]\s*$/,// Ends with sentence terminator
/\bconclusion\b/i,// Has conclusion
/\bsummary\b/i// Has summary
];return completionPatterns.some(pattern=>pattern.test(prompt))||prompt.trim().length>50;}/**
 * Extracts language hints from prompt content
 * 
 * @private
 * @param prompt - Prompt text
 * @param codeBlocks - Detected code blocks
 * @returns Array of language hints
 */function extractLanguageHints(prompt,codeBlocks){const hints=new Set();// Programming language detection
const langPatterns=[{pattern:/\b(javascript|js|typescript|ts|react|node)\b/i,hint:'JavaScript/TypeScript'},{pattern:/\b(python|py|django|flask)\b/i,hint:'Python'},{pattern:/\b(java|spring|android)\b/i,hint:'Java'},{pattern:/\b(html|css|sass|scss)\b/i,hint:'Web Technologies'},{pattern:/\b(sql|database|mysql|postgres)\b/i,hint:'Database'},{pattern:/\b(math|mathematics|calculus|algebra)\b/i,hint:'Mathematics'},{pattern:/\b(physics|chemistry|biology)\b/i,hint:'Science'}];for(const{pattern,hint}of langPatterns){if(pattern.test(prompt)){hints.add(hint);}}// Analyze code blocks for language hints
for(const block of codeBlocks){const langMatch=block.match(/```(\w+)/);if(langMatch){hints.add(langMatch[1]);}}return Array.from(hints);}// ============================================================================
// Node and Status Functions
// ============================================================================
/**
 * Determines if a content node should have a generation prompt
 * 
 * @param nodeData - Content node data to analyze
 * @returns True if the node type typically requires a prompt
 * 
 * @example
 * ```typescript
 * const needsPrompt = isPromptExpected({ type: 'lesson', requiresGeneration: true });
 * console.log(needsPrompt); // true
 * ```
 */export function isPromptExpected(nodeData){try{var _nodeData$generationC,_nodeData$generationC2,_nodeData$generationC3;if(!nodeData||typeof nodeData!=='object'){return false;}// Check explicit generation configuration
if(((_nodeData$generationC=nodeData.generationConfig)===null||_nodeData$generationC===void 0?void 0:_nodeData$generationC.requiresPrompt)===true){return true;}if(((_nodeData$generationC2=nodeData.generationConfig)===null||_nodeData$generationC2===void 0?void 0:_nodeData$generationC2.requiresPrompt)===false){return false;}// Check if node requires generation
if(nodeData.requiresGeneration===false){return false;}// Check node type
if(nodeData.type&&PROMPT_REQUIRED_TYPES.includes(nodeData.type.toLowerCase())){return true;}// Container nodes typically don't need prompts
if(nodeData.isContainer===true){return false;}// Check status - completed nodes might not need prompts
if(nodeData.status==='completed'||nodeData.status==='published'){return false;}// Default to expecting prompts for generation-related nodes
return nodeData.requiresGeneration===true||((_nodeData$generationC3=nodeData.generationConfig)===null||_nodeData$generationC3===void 0?void 0:_nodeData$generationC3.autoGenerate)===true;}catch(error){console.error('Error checking if prompt is expected:',error);return false;}}/**
 * Returns display status information for prompt UI components
 * 
 * @param prompt - Optional prompt string to analyze
 * @returns Status object with display information
 * 
 * @example
 * ```typescript
 * const status = getPromptDisplayStatus("Generate a lesson...");
 * console.log(status.status); // "ready"
 * console.log(status.message); // "Prompt ready"
 * ```
 */export function getPromptDisplayStatus(prompt){try{// Handle empty/missing prompt
if(!prompt||typeof prompt!=='string'||prompt.trim().length===0){return{status:'empty',message:'No prompt provided',className:'prompt-status-empty',isActionable:false,ariaLabel:'Empty prompt - no generation instructions available',icon:'empty'};}const trimmedPrompt=prompt.trim();// Check for loading indicators
if(trimmedPrompt.includes('...generating')||trimmedPrompt.includes('loading')){return{status:'loading',message:'Generating prompt...',className:'prompt-status-loading',isActionable:false,ariaLabel:'Prompt is being generated',icon:'loading'};}// Check for error indicators
if(trimmedPrompt.toLowerCase().includes('error')||trimmedPrompt.toLowerCase().includes('failed')){return{status:'error',message:'Prompt generation failed',className:'prompt-status-error',isActionable:true,ariaLabel:'Prompt generation failed - click to retry',icon:'error'};}// Analyze prompt for truncation
const analysis=analyzePrompt(trimmedPrompt);const isTruncated=analysis.length>DEFAULT_MAX_LENGTH;if(isTruncated){return{status:'truncated',message:`Prompt ready (${analysis.wordCount} words)`,className:'prompt-status-truncated',isActionable:true,ariaLabel:`Prompt available with ${analysis.wordCount} words - click to view full prompt`,icon:'truncated'};}// Normal ready state
return{status:'ready',message:`Prompt ready (${analysis.wordCount} words)`,className:'prompt-status-ready',isActionable:true,ariaLabel:`Prompt available with ${analysis.wordCount} words - click to view or copy`,icon:'ready'};}catch(error){console.error('Error determining prompt display status:',error);return{status:'error',message:'Status check failed',className:'prompt-status-error',isActionable:false,ariaLabel:'Unable to determine prompt status',icon:'error'};}}// ============================================================================
// Accessibility Helpers
// ============================================================================
/**
 * Generates accessibility-friendly prompt description
 * 
 * @param prompt - Prompt to describe
 * @returns Screen reader friendly description
 * 
 * @example
 * ```typescript
 * const description = getPromptAccessibilityDescription("# Lesson\nContent...");
 * // Returns: "Markdown formatted prompt with 2 lines and 15 words"
 * ```
 */export function getPromptAccessibilityDescription(prompt){try{if(!prompt||typeof prompt!=='string'){return'Empty prompt';}const analysis=analyzePrompt(prompt);const parts=[];// Format information
if(analysis.format!=='plain'){parts.push(`${analysis.format} formatted`);}parts.push('prompt');// Length information
if(analysis.lineCount>1){parts.push(`with ${analysis.lineCount} lines`);}parts.push(`and ${analysis.wordCount} words`);// Special features
if(analysis.hasCodeBlocks){parts.push('containing code examples');}// Complexity
if(analysis.complexity!=='simple'){parts.push(`of ${analysis.complexity} complexity`);}return parts.join(' ');}catch(error){console.error('Error creating accessibility description:',error);return'Prompt content';}}// ============================================================================
// Performance Optimizations
// ============================================================================
/**
 * Memoization cache for expensive operations
 * 
 * @private
 */const analysisCache=new Map();const MAX_CACHE_SIZE=100;/**
 * Cached version of analyzePrompt for performance optimization
 * 
 * @param prompt - Prompt to analyze
 * @returns Cached or newly computed analysis
 */export function analyzePromptCached(prompt){try{if(!prompt||typeof prompt!=='string'){return createEmptyAnalysis();}const cacheKey=prompt.trim();// Check cache first
if(analysisCache.has(cacheKey)){return analysisCache.get(cacheKey);}// Compute analysis
const analysis=analyzePrompt(cacheKey);// Cache management
if(analysisCache.size>=MAX_CACHE_SIZE){const firstKey=analysisCache.keys().next().value;analysisCache.delete(firstKey);}analysisCache.set(cacheKey,analysis);return analysis;}catch(error){console.error('Error in cached prompt analysis:',error);return createEmptyAnalysis();}}/**
 * Clears the analysis cache (useful for memory management)
 * 
 * @example
 * ```typescript
 * clearAnalysisCache(); // Frees up memory used by cached analyses
 * ```
 */export function clearAnalysisCache(){analysisCache.clear();}// ============================================================================
// Export Summary
// ============================================================================
/**
 * Complete set of utility functions for generationPrompt handling:
 *
 * Validation:
 * - validatePromptData: Validates prompt format and content
 * - sanitizePrompt: Sanitizes prompt data for safe display
 * - detectEncodingIssues: Detects string encoding corruption issues
 *
 * Display:
 * - truncatePrompt: Truncates long prompts with ellipsis
 * - formatPromptForDisplay: Formats prompt for user-friendly display
 *
 * Interaction:
 * - copyPromptToClipboard: Copies prompt to clipboard with error handling
 *
 * Analysis:
 * - analyzePrompt: Analyzes prompt characteristics
 * - analyzePromptCached: Performance-optimized cached analysis
 *
 * Status:
 * - isPromptExpected: Determines if a node should have a prompt
 * - getPromptDisplayStatus: Returns display status for UI
 *
 * Accessibility:
 * - getPromptAccessibilityDescription: Screen reader friendly descriptions
 *
 * Performance:
 * - clearAnalysisCache: Memory management for cached analyses
 */